import { useRef, useCallback } from 'react';
import { HeroSection, PartnersGrid, FeaturesSection, InquiryForm, CTASection, SectionWrapper, SectionTitle } from '@/components/partners';
import { SkipLink } from '@/components/ui/SkipLink';
import { useWebVitals } from '@/features/partners/hooks/useWebVitals';
import { usePartnersPageContent } from '@/features/partners/hooks/usePartnersPage';

export const PartnersPage = () => {
    // Track Core Web Vitals
    useWebVitals();
    const { partnerPageContent } = usePartnersPageContent();

    const formRef = useRef<HTMLDivElement>(null);

    const scrollToForm = useCallback(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth' });
        // Focus the first input after scroll
        setTimeout(() => {
            const firstInput = formRef.current?.querySelector('input');
            firstInput?.focus();
        }, 500);
    }, []);

    return (
        <>
            {/* Skip to main content link */}
            <SkipLink href="#main-content">Skip to main content</SkipLink>

            <main id="main-content">
                {/* Hero */}
                <HeroSection onCtaClick={scrollToForm} />

                {/* Intro + Highlights */}
                <FeaturesSection />

                {/* Partners List */}
                <PartnersGrid
                    title={partnerPageContent.logoGroup.title}
                    subtitle={partnerPageContent.logoGroup.description}
                    categoryLabel="Partner category"
                />

                {/* Inquiry Form */}
                <SectionWrapper id="inquire" background="white">
                    <SectionTitle
                        title={partnerPageContent.inquiry.title}
                        subtitle={partnerPageContent.inquiry.description}
                    />
                    <ul className="mx-auto mb-8 max-w-3xl space-y-3 text-sm text-muted-foreground md:text-base">
                        {partnerPageContent.inquiry.bulletPoints.map((point) => (
                            <li key={point} className="rounded-2xl bg-muted/50 px-4 py-3">
                                {point}
                            </li>
                        ))}
                    </ul>
                    <div ref={formRef} className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-6 md:p-10">
                        <InquiryForm />
                    </div>
                </SectionWrapper>

                {/* Final CTA */}
                <CTASection onCtaClick={scrollToForm} />
            </main>
        </>
    );
};
